import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../../Router';
import { Survey } from '../../components';
import { ActionSegment, ErrorTypes, SurveyName, SurveyType } from '@reviews/interfaces';
import segmentService from '../../services/segment/segmentService';
import _ from 'lodash';
import { Spinner } from '../../assests/icons';
import { getDisposition } from '../../utils/survey.util';
import { FrontendUtils } from '@reviews/frontend';
import { useGetSurveyForReservationQuery } from '../../services';
import { MessagePanel } from '../../components/MessagePanel/MessagePanel';
import { DetailedError } from '@reviews/interfaces';

export const SurveyView: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const tracked = useRef<boolean>(false);

    const reservationId = searchParams.get('reservation');
    const confirmationCode = searchParams.get('confirmation') ?? '';
    const nsuHash = searchParams.get('nsu') ?? '';
    const disposition = searchParams.get('disposition') ?? '';
    const starRating = searchParams.get('star-rating') ?? null;
    const isPal = searchParams.get('pal') === 'true' || searchParams.get('pal') === '1';
    const withContent = searchParams.get('with_content') === 'true' || searchParams.get('with_content') === '1';
    const reviewRequestOrigin = searchParams.get('review_request_origin') ?? undefined;

    const requiredComment = true;
    const dispositionTemplate = getDisposition(starRating, isPal);

    // If no star-rating, check if review already exists before redirecting to rating selection
    // Use a default survey name to check for errors (like RESERVATION_ALREADY_ANSWERED)
    const shouldCheckForExistingReview = !!reservationId && !starRating;
    const { error: checkError, isLoading: isChecking } = useGetSurveyForReservationQuery(
        {
            type: SurveyType.REVIEW,
            reservationId: reservationId ? +reservationId : 0,
            confirmationCode,
            nsuHash,
            name: SurveyName.REVIEW_POSITIVE_DISPOSITION, // Use default to check for errors
            isPal,
            withContent,
        },
        { skip: !shouldCheckForExistingReview } // Only run if we need to check
    );

    // Redirect to rating selection if no rating is provided AND no error exists
    useEffect(() => {
        if (shouldCheckForExistingReview && !isChecking && !checkError) {
            // No error means we can proceed to rating selection
            const ratingSelectionParams = new URLSearchParams();
            ratingSelectionParams.set('reservation', reservationId!);
            if (confirmationCode) ratingSelectionParams.set('confirmation', confirmationCode);
            if (nsuHash) ratingSelectionParams.set('nsu', nsuHash);
            if (disposition) ratingSelectionParams.set('disposition', disposition);
            if (isPal) ratingSelectionParams.set('pal', 'true');
            if (withContent) ratingSelectionParams.set('with_content', 'true');
            if (reviewRequestOrigin) ratingSelectionParams.set('review_request_origin', reviewRequestOrigin);
            navigate(`${AppRoutes.RATING_SELECTION}?${ratingSelectionParams.toString()}`);
        }
    }, [
        shouldCheckForExistingReview,
        isChecking,
        checkError,
        reservationId,
        confirmationCode,
        nsuHash,
        disposition,
        isPal,
        withContent,
        reviewRequestOrigin,
        navigate,
    ]);

    useEffect(() => {
        FrontendUtils.SharedUtils.setBaseItemForTracking(reservationId, confirmationCode, nsuHash, disposition, starRating);
        if (!tracked.current) {
            const baseTrackingObj: { [key: string]: string | null } = {
                reservation_id: reservationId,
                confirmation_code: confirmationCode,
                nsu: nsuHash,
                disposition: disposition,
                star_rating: starRating,
            };
            segmentService.trackPage();
            segmentService.trackEvent({ action: ActionSegment.PAGE_LOAD, ...baseTrackingObj });
            //dataLayerService.trackEvent(featureFlagName, fv.variant);
            tracked.current = true;
        }
    }, [confirmationCode, disposition, nsuHash, reservationId, starRating]);

    useEffect(() => {
        if (!reservationId) {
            navigate(AppRoutes.HOME);
        }
    }, [navigate, reservationId]);

    if (!reservationId || _.isNil(requiredComment)) {
        return (
            <div className="loading">
                <Spinner />
            </div>
        );
    }

    // If checking for existing review, show loading while checking
    if (shouldCheckForExistingReview && isChecking) {
        return (
            <div className="loading">
                <Spinner />
            </div>
        );
    }

    // show error message
    if (shouldCheckForExistingReview && !isChecking && checkError) {
        const errorKind = (checkError as DetailedError)?.code;
        if (
            errorKind === ErrorTypes.RESERVATION_ALREADY_ANSWERED ||
            errorKind === ErrorTypes.REVIEW_EXPIRED ||
            errorKind === ErrorTypes.INVALID_PAL_RESERVATION
        ) {
            return <MessagePanel kind={errorKind as ErrorTypes} action={ActionSegment.PAGE_LOAD} />;
        }
    }

    // after redirect show loading
    if (!starRating && !shouldCheckForExistingReview) {
        return (
            <div className="loading">
                <Spinner />
            </div>
        );
    }

    const footer = document.getElementById('footer');
    if (footer) {
        footer.classList.remove('fix-bottom');
    }

    return (
        <Survey
            reservationId={+reservationId}
            confirmationCode={confirmationCode}
            nsuHash={nsuHash}
            type={SurveyType.REVIEW}
            surveyName={dispositionTemplate}
            commentRequired={requiredComment}
            isPal={isPal}
            withContent={withContent}
        />
    );
};
